import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FaDownload, FaFilePdf, FaEdit, FaSpinner } from 'react-icons/fa';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

/**
 * Delay (ms) before revoking a Blob object URL created for a programmatic open/download.
 * Gives the browser time to start consuming the URL before it is released.
 */
const OBJECT_URL_CLEANUP_DELAY_MS = 10000;

/**
 * Flatten ReactMarkdown children (which may be a string, a React element, or an
 * array of these) down to a plain text label — typically the attachment filename.
 */
const extractText = (children) => {
    if (children == null) return '';
    if (typeof children === 'string') return children;
    if (Array.isArray(children)) return children.map(extractText).join('');
    if (typeof children === 'object' && children.props) return extractText(children.props.children);
    return String(children);
};

/**
 * Determine whether a resolved attachment is a PDF (eligible for annotation).
 */
const isPdfAttachment = (att) => {
    if (!att) return false;
    return Boolean(att.contentType?.toLowerCase().includes('pdf'))
        || Boolean(att.fileName?.toLowerCase().endsWith('.pdf'));
};

/**
 * CommentAttachmentLink
 *
 * Renders a hyperlink inside a comment body. When the link points at an action-item
 * attachment, the click is intercepted and the file is fetched through the
 * authenticated, Graph-proxied app endpoint and opened locally — so the reader never
 * needs direct SharePoint permissions. PDF attachments additionally show an inline
 * "Annotate" affordance that opens the PDF annotation modal with a fully-resolved
 * attachment object (fixing the "no PDF to annotate" caveat).
 *
 * Non-attachment links (plain external URLs) fall through to a normal anchor.
 *
 * @param {Object}   props
 * @param {string}   props.href              - The link target from the markdown source.
 * @param {*}        props.children          - The link label (usually the filename).
 * @param {Function} props.resolveAttachment - (href, label) => attachment|null. Maps a link to a
 *                                              known attachment ({ attachmentId, downloadUrl, fileName, contentType }).
 * @param {Function} [props.onAnnotate]      - (attachment) => void. Opens the PDF annotation modal.
 */
const CommentAttachmentLink = ({ href, children, resolveAttachment, onAnnotate }) => {
    const { apiFetch } = useAuth();
    const [busy, setBusy] = useState(false);

    const label = useMemo(() => extractText(children), [children]);
    const attachment = useMemo(
        () => (resolveAttachment ? resolveAttachment(href, label) : null),
        [resolveAttachment, href, label]
    );

    /**
     * Fetch the attachment through the authenticated app endpoint and open it locally.
     * Opens viewable types (PDF/images) in a new tab; falls back to a save-as download
     * if the browser blocks the popup.
     */
    const handleOpen = useCallback(async (e) => {
        if (!attachment?.downloadUrl) return; // plain link handled by the anchor itself
        e.preventDefault();
        e.stopPropagation();

        setBusy(true);
        try {
            const response = await apiFetch(attachment.downloadUrl);
            if (!response.ok) {
                toast.error(`Could not open file (HTTP ${response.status}).`);
                return;
            }
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            // Try to open in a new tab so the user can view the file locally.
            const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
            if (!opened) {
                // Popup blocked — fall back to a programmatic save-as download.
                const anchor = document.createElement('a');
                anchor.href = objectUrl;
                anchor.download = attachment.fileName || label || 'download';
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
            }

            setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_CLEANUP_DELAY_MS);
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to open the file. Please try again.');
        } finally {
            setBusy(false);
        }
    }, [apiFetch, attachment, label]);

    const handleAnnotate = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (attachment && onAnnotate) onAnnotate(attachment);
    }, [attachment, onAnnotate]);

    // Not a known attachment — render a normal external link.
    if (!attachment) {
        return (
            <a href={href} target='_blank' rel='noopener noreferrer'>
                {children}
            </a>
        );
    }

    const pdf = isPdfAttachment(attachment);

    return (
        <span className='comment-attachment-link'>
            <a
                href={attachment.downloadUrl}
                className='comment-attachment-anchor'
                onClick={handleOpen}
                title={`Open ${attachment.fileName || label} locally`}
            >
                {busy ? <FaSpinner className='spin' size={12} /> : (pdf ? <FaFilePdf size={12} /> : <FaDownload size={12} />)}
                <span className='comment-attachment-name'>{children}</span>
            </a>
            {pdf && onAnnotate && (
                <button
                    type='button'
                    className='comment-attachment-annotate'
                    onClick={handleAnnotate}
                    title='Annotate this PDF'
                >
                    <FaEdit size={11} /> Annotate
                </button>
            )}
        </span>
    );
};

CommentAttachmentLink.propTypes = {
    href: PropTypes.string,
    children: PropTypes.node,
    resolveAttachment: PropTypes.func,
    onAnnotate: PropTypes.func
};

/**
 * CommentAttachmentImage
 *
 * Renders an inline image referenced from a comment body. When the image source is an
 * authenticated app endpoint (e.g. an image dropped into a comment, now linked via
 * /api/ActionItems/attachments/{id}/download), the bytes are fetched with the JWT via
 * apiFetch and shown from an in-memory Blob URL — a plain <img src> cannot send the
 * Authorization header and would 401. Non-API sources render as a normal <img>.
 *
 * @param {Object} props
 * @param {string} props.src - Image source URL.
 * @param {string} [props.alt] - Alternate text.
 */
const CommentAttachmentImage = ({ src, alt }) => {
    const { apiFetch } = useAuth();
    const [authedSrc, setAuthedSrc] = useState(null);
    const [failed, setFailed] = useState(false);
    const objectUrlRef = useRef(null);

    // Only API-relative attachment URLs need an authenticated fetch.
    const needsAuth = typeof src === 'string' && src.startsWith('/api/');

    useEffect(() => {
        // Non-API sources render directly (handled in JSX); nothing to fetch.
        if (!needsAuth) return undefined;

        let cancelled = false;

        const revoke = () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };

        (async () => {
            try {
                const response = await apiFetch(src);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                if (cancelled) return;
                const objectUrl = URL.createObjectURL(blob);
                objectUrlRef.current = objectUrl;
                setFailed(false);
                setAuthedSrc(objectUrl);
            } catch (err) {
                Sentry.captureException(err);
                if (!cancelled) setFailed(true);
            }
        })();

        return () => {
            cancelled = true;
            revoke();
        };
    }, [src, needsAuth, apiFetch]);

    if (failed) {
        return <span className='comment-image-error'>[Image unavailable: {alt || 'attachment'}]</span>;
    }

    // Non-API images render straight from their source.
    if (!needsAuth) {
        return <img src={src} alt={alt || ''} className='comment-inline-image' loading='lazy' />;
    }

    // Authenticated images wait for the blob URL produced by the effect.
    if (!authedSrc) {
        return <span className='comment-image-loading'>Loading image…</span>;
    }
    return <img src={authedSrc} alt={alt || ''} className='comment-inline-image' loading='lazy' />;
};

CommentAttachmentImage.propTypes = {
    src: PropTypes.string,
    alt: PropTypes.string
};

export default CommentAttachmentLink;
export { CommentAttachmentImage };
